import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import music from '../icons/music.svg'
import LoadingIcons from 'react-loading-icons'

const RESERVED_HEIGHT = 'min-h-32 md:min-h-24';

const Listening = () => {
  const [songName, setSongName] = useState(null);
  const [cover, setCover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState(null);

  const wrapperRef = useRef(null);
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollDuration, setScrollDuration] = useState(8);

  useEffect(() => {
    const url = process.env.REACT_APP_BACKEND_URL;
    const fetchData = async () => {
      try {
        const response = await axios.get(`${url}/`); // Replace with your backend endpoint
        const track = response.data.track
        const songName = track.name
        const cover = track.image[2]["#text"]
        const youtubeUrl = response.data.youtube?.url || null
        setYoutubeUrl(youtubeUrl)
        setIsListening(track?.["@attr"]?.nowplaying === "true")
        setSongName(songName);
        setCover(cover);

      } catch (err) {
        setError(err); // Set error if there's an issue with fetching
      } finally {
        setLoading(false); // Turn off loading state
      }
    };

    fetchData(); // Call the function to fetch data
  }, []); // Empty dependency array means it runs only on component mount

  useEffect(() => {
    const measure = () => {
      if (!textRef.current || !wrapperRef.current) return;
      const overflowing = textRef.current.scrollWidth > wrapperRef.current.clientWidth;
      setIsOverflowing(overflowing);
      if (overflowing) {
        setScrollDuration(Math.max(6, textRef.current.scrollWidth / 40));
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [songName]);


  if (error) return <div className={`bg-slate-900 ${RESERVED_HEIGHT}`}></div>;

  return (
    <div className={`bg-slate-900 py-2 flex items-center justify-center ${RESERVED_HEIGHT}`}>
      {
        loading ? (
          <LoadingIcons.Bars height={"64"} className='mx-auto'/>
        ):(
          <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-2 px-4 md:px-0 w-full min-w-0 md:w-2/5 mx-auto">

            {/* Row 1 (mobile) / left group (desktop): label + spinning disc + album cover */}
            <div className="flex items-center justify-center gap-2 font-mono text-teal-300 shrink-0">
              <span>Listening to:</span>
              <div className="relative w-[115.2px] h-16 md:w-36 md:h-20 shrink-0">
                <img
                  src={music}
                  alt=""
                  loading="lazy"
                  width="80"
                  height="80"
                  className={`absolute left-0 top-0 w-16 h-16 md:w-20 md:h-20 ${isListening ? 'animate-spin [animation-duration:2.00s] motion-reduce:animate-none' : ''}`}
                />
                <img
                  src={cover}
                  alt={songName ? `Album art for ${songName}` : 'Album art'}
                  loading="lazy"
                  width="80"
                  height="80"
                  className="absolute left-[51.2px] top-0 w-16 h-16 md:left-16 md:w-20 md:h-20 shadow-[0_2px_8px_rgba(20,184,166,0.5)]"
                />
              </div>
            </div>

            {/* Row 2 (mobile) / right group (desktop): song name (auto-scrolls if too long) + Play */}
            <div className={`flex items-center justify-center gap-2 min-w-0 ${isOverflowing ? 'md:flex-1' : ''}`}>
              <div ref={wrapperRef} className={`overflow-hidden min-w-0 text-left ${isOverflowing ? 'flex-1' : ''}`}>
                <div
                  className={`flex ${isOverflowing ? 'animate-marquee motion-reduce:animate-none' : ''}`}
                  style={isOverflowing ? { animationDuration: `${scrollDuration}s` } : undefined}
                >
                  <span ref={textRef} className={`font-sans text-slate-200 font-medium whitespace-nowrap shrink-0 ${isOverflowing ? 'pr-8' : ''}`}>
                    {songName}
                  </span>
                  {isOverflowing && (
                    <span aria-hidden="true" className="font-sans text-slate-200 font-medium whitespace-nowrap pr-8 shrink-0">
                      {songName}
                    </span>
                  )}
                </div>
              </div>
              {youtubeUrl && (
                <a
                  href={youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 border-2 border-teal-300 px-3 py-1 font-mono text-sm text-teal-300 shadow-teal-300 shadow-md hover:shadow-sm hover:shadow-teal-300"
                >
                  Play
                </a>
              )}
            </div>
          </div>
        )
      }
    </div>
  );
};

export default Listening;
